import { Stack, ScrollArea } from "@mantine/core"
import { IconEdit, IconPlus } from "@tabler/icons-react"
import {
  ActionIcon,
  AreYouSureModal,
  Button,
  DragEndResult,
  DraggableItem,
  DragNDrop,
  InfoBox,
} from "@tfg/ui"
import { AxiosError } from "axios"
import { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findAllProductPageComponentsQueryKey,
  getProductPageComponentsQueryKey,
  GetProductPageComponentsResponseDtoComponentTypeEnumKey,
} from "~/gen/api"
import { useDeleteProductPageComponent } from "~/gen/api/hooks/useDeleteProductPageComponent"
import { useUpdateProductPageComponents } from "~/gen/api/hooks/useUpdateProductPageComponents"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"
import { isComponentEditable } from "~/pages/Brand/CommunicationStudio/utils/componentEditability"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type PreviousState = {
  productPageComponentIds: string[]
  language: string
}

export const ComponentsEditor = () => {
  const {
    productSheetGroupId,
    productPageComponents,
    openEditModal,
    openCreateModal,
  } = useComponentCreation()
  const { t, i18n } = useTranslation("communicationStudio")
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [modalHandlers, setModalHandlers] = useState<{
    handleClose: () => void
    handleConfirm: () => void
  } | null>(null)
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null
  )
  const [pageComponents, setPageComponents] = useState<
    (DraggableItem & {
      componentType: GetProductPageComponentsResponseDtoComponentTypeEnumKey
    })[]
  >([])
  const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false)

  const previousStateRef = useRef<PreviousState>({
    productPageComponentIds: [],
    language: i18n.language,
  })

  const { mutate: updatePageComponents } = useUpdateProductPageComponents({
    mutation: {
      onSuccess: () => {
        setIsOptimisticUpdate(false)
        showSuccessNotification({
          title: t("tabs.content.updateProductPageComponents.success.title"),
          message: t(
            "tabs.content.updateProductPageComponents.success.message"
          ),
        })
        setTimeout(() => {
          setIsOptimisticUpdate(false)
        }, 100)
      },
      onError: (error) => {
        setIsOptimisticUpdate(false)
        queryClient.invalidateQueries({
          queryKey: findAllProductPageComponentsQueryKey({
            displaySettingsId: productSheetGroupId,
          }),
        })
        showAxiosError({
          error: error as AxiosError,
          showNotification: true,
          notificationMessage: "Failed to update product page components",
        })
      },
    },
  })
  const { mutate: deleteComponent, isPending: isDeletingComponent } =
    useDeleteProductPageComponent({
      mutation: {
        onSuccess: () => {
          setIsOptimisticUpdate(false)
          queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(productSheetGroupId),
          })
          showSuccessNotification({
            title: t(
              "tabs.content.updateProductPageComponents.deleteComponent.success.title"
            ),
            message: t(
              "tabs.content.updateProductPageComponents.deleteComponent.success.message"
            ),
          })
        },
        onError: (error) => {
          showAxiosError({
            error: error as AxiosError,
            showNotification: true,
            notificationMessage: t(
              "tabs.content.updateProductPageComponents.deleteComponent.error.message"
            ),
            notificationTitle: t(
              "tabs.content.updateProductPageComponents.deleteComponent.error.title"
            ),
          })
        },
      },
    })

  useEffect(() => {
    if (productPageComponents && !isOptimisticUpdate) {
      const sortedComponents = productPageComponents
        .sort((a, b) => a.order - b.order)
        .map((component) => {
          const isEditable = isComponentEditable(component.componentType)

          return {
            id: component.id,
            label: t(`tabs.content.components.${component.componentType}`),
            position: component.order,
            componentType: component.componentType,
            itemProps: {
              bg: "gray.0",
            },
            ...(isEditable && {
              cta: (
                <ActionIcon
                  icon={<IconEdit />}
                  onClick={() => openEditModal(component)}
                  aria-label="Edit component"
                />
              ),
            }),
          }
        })

      const newIds = sortedComponents
        .map((c) => c.id)
        .sort()
        .join(",")
      const previousIds = previousStateRef.current.productPageComponentIds
        .sort()
        .join(",")
      const hasStructuralChange =
        previousStateRef.current.productPageComponentIds.length === 0 || // Initial load
        newIds !== previousIds || // Different IDs or count
        previousStateRef.current.language !== i18n.language // Different languages

      if (hasStructuralChange) {
        setPageComponents(sortedComponents)
        previousStateRef.current = {
          productPageComponentIds: sortedComponents.map((c) => c.id),
          language: i18n.language,
        }
      }
    }
  }, [
    productPageComponents,
    isOptimisticUpdate,
    t,
    openEditModal,
    i18n.language,
  ])

  const handleDragEnd = (result: DragEndResult) => {
    const { destination, item } = result
    if (!destination) return

    const sourceIndex = pageComponents.findIndex((comp) => comp.id === item.id)
    const destIndex = destination.index
    setIsOptimisticUpdate(true)

    const newOrder = [...pageComponents]
    const [movedItem] = newOrder.splice(sourceIndex, 1)
    newOrder.splice(destIndex, 0, movedItem)
    const updatedComponents = newOrder.map((component, index) => ({
      ...component,
      position: index + 1,
    }))

    setPageComponents(updatedComponents)
    updatePageComponents({
      data: {
        productPageComponents: updatedComponents.map((component) => ({
          productPageComponentId: component.id,
          order: component.position,
          componentType: component.componentType,
        })),
      },
    })
  }

  const confirmDelete = (id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setSelectedComponentId(id)
      setIsConfirmModalOpen(true)

      const handleClose = () => {
        setIsConfirmModalOpen(false)
        resolve(false)
      }

      const handleConfirm = () => {
        setIsConfirmModalOpen(false)
        resolve(true)
      }
      setModalHandlers({ handleClose, handleConfirm })
    })
  }

  return (
    <>
      {pageComponents && pageComponents.length > 0 ? (
        <Stack
          w="100%"
          align="flex-start"
          pos="relative"
          justify="flex-start"
          style={{ overflow: "hidden" }}
        >
          <ScrollArea h="60vh" w="100%" p="sm">
            <DragNDrop
              data={pageComponents}
              onDragEnd={handleDragEnd}
              showPosition
              onDelete={(id) => deleteComponent({ id })}
              beforeDelete={(id) => confirmDelete(id)}
              deletable={true}
            />
          </ScrollArea>
          <Button
            variant="tertiary"
            color="primary"
            icon={<IconPlus size={16} />}
            onClick={openCreateModal}
            disabled={isDeletingComponent}
          >
            {t("tabs.content.componentsEditor.createComponent")}
          </Button>
          <AreYouSureModal
            opened={isConfirmModalOpen}
            onClose={() => modalHandlers?.handleClose()}
            onConfirm={() => {
              if (!selectedComponentId) return
              modalHandlers?.handleConfirm()
            }}
            areYouSureText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.areYouSureText"
            )}
            confirmText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.confirmText"
            )}
            cancelText={t(
              "tabs.content.updateProductPageComponents.deleteComponent.cancelText"
            )}
          />
        </Stack>
      ) : (
        <InfoBox
          title="No components found"
          message="Please add a component to the product sheet"
        />
      )}
    </>
  )
}
